import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LiveStatus = () => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(timer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get Marseille time (CET/CEST)
  const marseilleTime = currentTime.toLocaleTimeString('en-US', {
    timeZone: 'Europe/Paris',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  });

  const isWorkingHours = () => {
    const hour = parseInt(marseilleTime.split(':')[0]);
    return hour >= 9 && hour <= 18; // 9 AM to 6 PM Marseille time
  };

  return (
    <motion.div
      className="fixed bottom-6 right-4 z-40 flex items-center space-x-3 bg-lightNavy/95 backdrop-blur-md px-4 py-2 rounded-full border border-accent/20 shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 2 }}
      whileHover={{ scale: 1.05 }}
    >
      {/* Status Indicator */}
      <div className="flex items-center space-x-2">
        <motion.div
          className={`w-2 h-2 rounded-full ${
            isOnline && isWorkingHours() 
              ? 'bg-green-500' 
              : isOnline 
                ? 'bg-yellow-500' 
                : 'bg-red-500'
          }`}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [1, 0.7, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <span className="text-xs font-mono text-lighterSlate">
          {isOnline
            ? isWorkingHours()
              ? t('status.availableForWork')
              : t('status.onlineAfterHours')
            : t('status.offline')
          }
        </span>
      </div>

      {/* Time in Marseille */}
      <div className="text-xs font-mono text-slate/60 border-l border-accent/20 pl-3">
        🇫🇷 {marseilleTime} CET
      </div>

      {/* Pulse animation for availability */}
      {isOnline && isWorkingHours() && (
        <motion.div
          className="absolute inset-0 rounded-full border border-green-500/30"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.5, 0, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeOut"
          }}
        />
      )}
    </motion.div>
  );
};

export default LiveStatus;